import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TypedChanges } from '@platform-frontend/util';
import { ReplaySubject } from 'rxjs';
import { tap } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'yat-email-clip',
  templateUrl: './email-clip.component.html',
  styleUrls: ['./email-clip.component.scss']
})
export class EmailClipComponent implements OnChanges, AfterViewInit, OnDestroy {
  constructor(private changeDetectorRef: ChangeDetectorRef, private elRef: ElementRef) {}
  @Input()
  id: string;

  @Input()
  email: string | null;

  @ViewChild('totalSpace')
  totalSpace: ElementRef;

  @ViewChild('spanName')
  spanName: ElementRef;

  @ViewChild('spanDomain')
  spanDomain: ElementRef;

  @ViewChild('at')
  at: ElementRef;

  name: string = '';
  domain: string = '';
  lockText: boolean = true;

  private emailChanged$: ReplaySubject<void> = new ReplaySubject<void>(1);
  private observer: ResizeObserver;

  ngOnChanges(changes: TypedChanges<EmailClipComponent>): void {
    if (changes.email) {
      this.setEmailParts();
    }
  }

  ngAfterViewInit(): void {
    this.emailChanged$
      .pipe(
        tap(() => {
          this.lockText = false;
          this.changeDetectorRef.detectChanges();

          const totalAvailableSpace: number = this.totalSpace.nativeElement.offsetWidth;
          const spanAtWidth: number = this.email ? this.at.nativeElement.offsetWidth : 0;

          for (let i = 0; i < 8; i++) {
            const spanNameWidth: number = this.spanName.nativeElement.offsetWidth;
            const spanDomainWidth: number = this.spanDomain.nativeElement.offsetWidth;
            const totalEmailWidth: number = spanNameWidth + spanDomainWidth + spanAtWidth;

            if (totalEmailWidth <= totalAvailableSpace) {
              break;
            }

            const spaceFraction: number = totalAvailableSpace / totalEmailWidth;

            if (this.name.length >= this.domain.length) {
              this.shortenNameByFraction(spaceFraction);
            } else {
              this.shortenDomainByFraction(spaceFraction);
            }

            this.changeDetectorRef.detectChanges();
          }

          this.lockText = true;
          this.changeDetectorRef.detectChanges();
        }),
        untilDestroyed(this)
      )
      .subscribe();

    this.observer = new ResizeObserver(() => this.setEmailParts());
    this.observer.observe(this.elRef.nativeElement.parentElement);
  }

  ngOnDestroy(): void {
    this.observer.disconnect();
  }

  private setEmailParts(): void {
    const [name, domain] = this.email?.split('@') ?? ['', ''];
    this.name = name;
    this.domain = domain;
    this.emailChanged$.next();
  }

  private shortenNameByFraction(spaceFraction: number): void {
    if (this.name.endsWith('…')) {
      this.name = this.name.substring(0, this.name.length - 1);
    }
    const nameFraction: number = Math.floor(this.name.length * spaceFraction);
    const nameEndIndex: number = nameFraction > 1 ? nameFraction - 1 : nameFraction;
    this.name = this.name.substring(0, nameEndIndex) + '…';
  }

  private shortenDomainByFraction(spaceFraction: number): void {
    if (this.domain.startsWith('…')) {
      this.domain = this.domain.substring(1, this.domain.length);
    }
    const domainLength: number = this.domain.length;
    const domainFraction: number = Math.floor(domainLength * spaceFraction);
    const domainStartIndex: number = domainFraction > 1 ? domainLength - domainFraction + 1 : domainLength - domainFraction;
    this.domain = '…' + this.domain.substring(domainStartIndex, domainLength);
  }
}
